import React, { useState, useEffect } from 'react';
import { Camera, ArrowLeft, Lock, Eye, EyeOff, Plus, Edit3, Trash2, Check, X, Shield, ShieldCheck, Share2, Copy, ExternalLink, Mail } from 'lucide-react';

const PhotoGallery = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [password, setPassword] = useState('');
  const [currentView, setCurrentView] = useState('albums');
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Data from API
  const [albums, setAlbums] = useState([]);
  const [collections, setCollections] = useState([]);
  const [editingCollection, setEditingCollection] = useState(null);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [selectedAlbumsForCollection, setSelectedAlbumsForCollection] = useState([]);
  const [availablePhotos, setAvailablePhotos] = useState([]);
  const [selectedPhotosForAlbum, setSelectedPhotosForAlbum] = useState([]);
  const [albumPassword, setAlbumPassword] = useState('');
  const [showAlbumPassword, setShowAlbumPassword] = useState(false);
  const [albumEmail, setAlbumEmail] = useState('');
  const [albumAuthTokens, setAlbumAuthTokens] = useState({});
  const [accessDuration, setAccessDuration] = useState('1440'); // Default 24 hours in minutes
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalAlbum, setShareModalAlbum] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [shareDuration, setShareDuration] = useState('1440');
  const [isSharedView, setIsSharedView] = useState(false);
  const [sharedToken, setSharedToken] = useState('');
  const [sortOrder, setSortOrder] = useState('oldest'); // 'newest', 'oldest', 'name'
  const [albumPhotosSortOrder, setAlbumPhotosSortOrder] = useState('oldest'); // 'newest', 'oldest', 'name'
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [showThumbnailSelector, setShowThumbnailSelector] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [showUserManager, setShowUserManager] = useState(false);
  const [userManagerAlbum, setUserManagerAlbum] = useState(null);
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  const [showSharedLinkManager, setShowSharedLinkManager] = useState(false);
  const [sharedLinks, setSharedLinks] = useState([]);
  const [showPasswordManager, setShowPasswordManager] = useState(false);
  const [albumPasswords, setAlbumPasswords] = useState([]);
  const [passwordVisibility, setPasswordVisibility] = useState({});

  // Pagination for browse photos view
  const [currentPage, setCurrentPage] = useState(1);
  const [photosPerPage, setPhotosPerPage] = useState(100);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [jumpToPage, setJumpToPage] = useState('');
  const [compactView, setCompactView] = useState(false);

  // Collection management modals
  const [showCollectionSharedLinksModal, setShowCollectionSharedLinksModal] = useState(false);
  const [showCollectionVerifiedUsersModal, setShowCollectionVerifiedUsersModal] = useState(false);
  const [selectedCollectionForManagement, setSelectedCollectionForManagement] = useState(null);
  const [collectionSharedLinks, setCollectionSharedLinks] = useState([]);
  const [collectionVerifiedUsers, setCollectionVerifiedUsers] = useState([]);
  const [showCollectionPasswordManager, setShowCollectionPasswordManager] = useState(false);
  const [collectionPasswords, setCollectionPasswords] = useState([]);
  const [collectionPasswordVisibility, setCollectionPasswordVisibility] = useState({});

  // Video links management
  const [showVideoLinkModal, setShowVideoLinkModal] = useState(false);
  const [videoLinkAlbum, setVideoLinkAlbum] = useState(null);
  const [videoLinkUrl, setVideoLinkUrl] = useState('');

  // Logs management
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsAlbum, setLogsAlbum] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);

  // Folder selection for album creation
  const [availableFolders, setAvailableFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [showFolderSelection, setShowFolderSelection] = useState(false);

  // Scanning progress tracking
  const [scanProgress, setScanProgress] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState('');
  const [lastLoadedFolder, setLastLoadedFolder] = useState('');

  // API calls
  const apiCall = async (url, options = {}) => {
    console.log('API call to:', url, 'with options:', options);
    
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeout = options.timeout || 300000; // 5 minutes default, longer for photo scanning
    
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        signal: controller.signal,
        ...options
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('API Error:', err);
      
      if (err.name === 'AbortError') {
        const timeoutMessage = 'Photo scanning is taking longer than expected. This may be due to a large number of photos. Please try selecting a smaller folder or wait a moment and try again.';
        setError(timeoutMessage);
        throw new Error(timeoutMessage);
      } else {
        setError(err.message);
        throw err;
      }
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await apiCall('/api/auth', {
        method: 'POST',
        body: JSON.stringify({ password })
      });
      
      setIsAuthenticated(true);
      setUserType(result.userType);
      if (result.userType === 'admin') {
        setAdminToken(password); // Store admin password as token
      }
      setPassword('');
      loadAlbums();
      loadCollections();
    } catch (err) {
      setError('Invalid password');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const loadAlbums = async () => {
    setLoading(true);
    try {
      const albumsData = await apiCall('/api/albums');
      console.log('Loaded albums:', albumsData.map(album => ({
        id: album.id,
        name: album.name,
        isProtected: album.isProtected,
        hasPassword: !!album.password,
        photoCount: album.photo_count
      })));
      setAlbums(albumsData);
    } catch (err) {
      setError('Failed to load albums');
    } finally {
      setLoading(false);
    }
  };

  const loadCollections = async () => {
    setLoading(true);
    try {
      const collectionsData = await apiCall('/api/collections');
      console.log('Loaded collections:', collectionsData.map(collection => ({
        id: collection.id,
        name: collection.name,
        isProtected: collection.isProtected,
        albumCount: collection.album_count
      })));
      setCollections(collectionsData);
    } catch (err) {
      setError('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableFolders = async () => {
    try {
      const foldersData = await apiCall('/api/folders');
      console.log('Loaded', foldersData?.length || 0, 'folders for selection');
      setAvailableFolders(foldersData || []);
    } catch (err) {
      console.error('Failed to load folders:', err);
      setError('Failed to load folders');
      setAvailableFolders([]); // Ensure state is set even on error
    }
  };

  // Progressive loading using Server-Sent Events
  const loadAvailablePhotosProgressive = async (folder = '', forceReload = false) => {
    // Skip if we already have photos from the same folder (unless forced)
    if (!forceReload && lastLoadedFolder === folder && availablePhotos.length > 0) {
      console.log(`Photos already loaded for folder '${folder}', skipping scan`);
      return;
    }

    setLoading(true);
    setIsScanning(true);
    setScanProgress(0);
    setScanStatus(folder ? `Scanning folder: ${folder}` : 'Scanning all photos');

    // Clear existing photos to show progressive loading
    setAvailablePhotos([]);
    setTotalPhotos(0);

    const streamedPhotos = [];

    try {
      const url = folder ? `/api/photos/scan-stream?folder=${encodeURIComponent(folder)}` : '/api/photos/scan-stream';

      const eventSource = new EventSource(url);

      return new Promise((resolve, reject) => {
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            switch (data.type) {
              case 'start':
                console.log('Starting scan:', data.message);
                setScanStatus(data.message);
                break;

              case 'photo':
                // Add photo immediately to the UI
                streamedPhotos.push(data.data);
                setAvailablePhotos([...streamedPhotos]);
                setTotalPhotos(streamedPhotos.length);
                console.log(`Photo found: ${data.data.filename}`);
                break;

              case 'progress':
                setScanProgress(data.data.percentage || 0);
                setScanStatus(`Scanning... ${data.data.processedPhotos}/${data.data.totalPhotos} photos`);
                break;

              case 'complete':
                console.log('Scan complete. Total photos:', streamedPhotos.length);
                setScanProgress(100);
                setScanStatus(`Found ${streamedPhotos.length} photos`);
                setLastLoadedFolder(folder);

                // Sort photos by creation date (oldest first)
                const sortedPhotos = [...streamedPhotos].sort((a, b) => {
                  const dateA = new Date(a.created);
                  const dateB = new Date(b.created);
                  return dateA - dateB;
                });
                setAvailablePhotos(sortedPhotos);
                setCurrentPage(1);

                // Clear scanning state after brief delay
                setTimeout(() => {
                  setIsScanning(false);
                  setScanProgress(0);
                  setScanStatus('');
                }, 1000);

                eventSource.close();
                resolve();
                break;

              case 'error':
                throw new Error(data.message);

              default:
                if (data.error) {
                  throw new Error(data.error);
                }
            }
          } catch (parseErr) {
            console.error('Error parsing SSE data:', parseErr);
          }
        };

        eventSource.onerror = (error) => {
          console.error('SSE Error:', error);
          eventSource.close();
          setIsScanning(false);
          setScanProgress(0);
          setScanStatus('');
          setError('Failed to load photos');
          reject(error);
        };
      });

    } catch (err) {
      console.error('Error starting progressive scan:', err);
      setIsScanning(false);
      setScanProgress(0);
      setScanStatus('');
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePhotos = async (folder = '', forceReload = false) => {
    // Skip if we already have photos from the same folder (unless forced)
    if (!forceReload && lastLoadedFolder === folder && availablePhotos.length > 0) {
      console.log(`Photos already loaded for folder '${folder}', skipping scan`);
      return;
    }

    setLoading(true);
    setIsScanning(true);
    setScanProgress(0);
    setScanStatus(folder ? `Scanning folder: ${folder}` : 'Scanning all photos');

    // Simulate progress updates during the API call
    const progressInterval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 90) return 90; // Don't go to 100% until we're done
        return prev + Math.random() * 15; // Random incremental progress
      });
    }, 200);

    try {
      const url = folder ? `/api/photos?folder=${encodeURIComponent(folder)}` : '/api/photos';
      const photosData = await apiCall(url, { timeout: 600000 }); // 10 minutes for photo scanning
      console.log('Loaded photos:', photosData.map(p => ({ id: p.id, filename: p.filename })));

      // Check for duplicate IDs
      const ids = photosData.map(p => p.id);
      const uniqueIds = [...new Set(ids)];
      if (ids.length !== uniqueIds.length) {
        console.error('DUPLICATE IDs FOUND!', {
          totalPhotos: ids.length,
          uniqueIds: uniqueIds.length,
          duplicates: ids.filter((id, index) => ids.indexOf(id) !== index)
        });
      } else {
        console.log('All photo IDs are unique');
      }

      // Complete the progress
      clearInterval(progressInterval);
      setScanProgress(100);
      setScanStatus(`Found ${photosData.length} photos`);

      setAvailablePhotos(photosData);
      setTotalPhotos(photosData.length);
      setCurrentPage(1); // Reset to first page when loading new photos
      setLastLoadedFolder(folder); // Track what folder we just loaded

      // Clear scanning state after a brief delay
      setTimeout(() => {
        setIsScanning(false);
        setScanProgress(0);
        setScanStatus('');
      }, 1000);
    } catch (err) {
      clearInterval(progressInterval);
      setIsScanning(false);
      setScanProgress(0);
      setScanStatus('');
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const authenticateAlbum = async (albumId, password, email) => {
    try {
      const body = { password, email };
      
      // Add shared token if this is a shared album
      if (selectedAlbum?.isShared && selectedAlbum?.sharedToken) {
        body.sharedToken = selectedAlbum.sharedToken;
      }
      
      const result = await apiCall(`/api/albums/${albumId}/auth`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      
      // Check if verification is required
      if (result.requiresVerification) {
        return { success: false, requiresVerification: true, message: result.message };
      }
      
      if (result.token) {
        setAlbumAuthTokens(prev => ({
          ...prev,
          [albumId]: result.token
        }));
        // Return the token so it can be used immediately
        return { success: true, token: result.token };
      }
      
      return { success: true, token: null };
    } catch (err) {
      setError('Invalid album password or email');
      return { success: false };
    }
  };

  const authenticateCollection = async (collectionId, password, email) => {
    try {
      const body = { password, email };
      
      // Add shared token if this is a shared collection
      if (selectedCollection?.isShared && selectedCollection?.sharedToken) {
        body.sharedToken = selectedCollection.sharedToken;
      }
      
      const result = await apiCall(`/api/collections/${collectionId}/auth`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      
      // Check if verification is required
      if (result.requiresVerification) {
        return { success: false, requiresVerification: true, message: result.message };
      }
      
      if (result.token) {
        // Store collection auth token (reusing album auth tokens structure)
        setAlbumAuthTokens(prev => ({
          ...prev,
          [`collection_${collectionId}`]: result.token
        }));
        // Return the token so it can be used immediately
        return { success: true, token: result.token };
      }
      
      return { success: true, token: null };
    } catch (err) {
      setError('Invalid collection password or email');
      return { success: false };
    }
  };

  const loadAlbumWithAuth = async (albumId) => {
    const token = albumAuthTokens[albumId];
    // If user is admin, use admin token; otherwise use album-specific token
    const authToken = userType === 'admin' ? adminToken : token;
    const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
    
    try {
      const albumData = await apiCall(`/api/albums/${albumId}`, { headers });
      return albumData;
    } catch (err) {
      if (err.message.includes('401')) {
        // Album requires authentication
        return null;
      }
      throw err;
    }
  };

  const createSharedLink = async (albumId, duration) => {
    try {
      const result = await apiCall(`/api/albums/${albumId}/share`, {
        method: 'POST',
        body: JSON.stringify({ accessDuration: duration })
      });
      return result;
    } catch (err) {
      setError('Failed to create shared link');
      throw err;
    }
  };

  const loadSharedAlbum = async (token) => {
    try {
      const albumData = await apiCall(`/api/shared/${token}`);
      return albumData;
    } catch (err) {
      setError('Shared link not found or expired');
      throw err;
    }
  };

  const loadSharedCollection = async (token) => {
    try {
      const collectionData = await apiCall(`/api/shared/collection/${token}`);
      return collectionData;
    } catch (err) {
      setError('Shared collection link not found or expired');
      throw err;
    }
  };

  const loadVerifiedUsers = async (albumId) => {
    try {
      const users = await apiCall(`/api/albums/${albumId}/verified-users`);
      setVerifiedUsers(users);
      return users;
    } catch (err) {
      setError('Failed to load verified users');
      throw err;
    }
  };

  const removeVerifiedUser = async (albumId, email) => {
    try {
      await apiCall(`/api/albums/${albumId}/verified-users/${encodeURIComponent(email)}`, {
        method: 'DELETE'
      });
      
      // Reload the verified users list
      await loadVerifiedUsers(albumId);
      setError(''); // Clear any existing errors
    } catch (err) {
      setError('Failed to remove user');
      throw err;
    }
  };

  const openUserManager = async (album) => {
    setUserManagerAlbum(album);
    setShowUserManager(true);
    await loadVerifiedUsers(album.id);
  };

  const loadSharedLinks = async () => {
    try {
      const headers = adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
      const links = await apiCall('/api/shared-links', { headers });
      setSharedLinks(links);
      return links;
    } catch (err) {
      setError('Failed to load shared links');
      throw err;
    }
  };

  const deleteSharedLink = async (token) => {
    try {
      const headers = adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
      await apiCall(`/api/shared-links/${token}`, {
        method: 'DELETE',
        headers
      });
      
      // Reload the shared links list
      await loadSharedLinks();
      setError(''); // Clear any existing errors
    } catch (err) {
      setError('Failed to delete shared link');
      throw err;
    }
  };

  const openSharedLinkManager = async () => {
    setShowSharedLinkManager(true);
    await loadSharedLinks();
  };

  const loadAlbumPasswords = async () => {
    try {
      const headers = adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
      const passwords = await apiCall('/api/album-passwords', { headers });
      setAlbumPasswords(passwords);
      return passwords;
    } catch (err) {
      setError('Failed to load album passwords');
      throw err;
    }
  };

  const openPasswordManager = async () => {
    setShowPasswordManager(true);
    setPasswordVisibility({});  // Reset visibility state when opening
    await loadAlbumPasswords();
  };

  const togglePasswordVisibility = (albumId) => {
    setPasswordVisibility(prev => ({
      ...prev,
      [albumId]: !prev[albumId]
    }));
  };

  const loadCollectionPasswords = async () => {
    try {
      const headers = adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
      const passwords = await apiCall('/api/collection-passwords', { headers });
      setCollectionPasswords(passwords);
      return passwords;
    } catch (err) {
      setError('Failed to load collection passwords');
      throw err;
    }
  };

  const openCollectionPasswordManager = async () => {
    setShowCollectionPasswordManager(true);
    setCollectionPasswordVisibility({});  // Reset visibility state when opening
    await loadCollectionPasswords();
  };

  const toggleCollectionPasswordVisibility = (collectionId) => {
    setCollectionPasswordVisibility(prev => ({
      ...prev,
      [collectionId]: !prev[collectionId]
    }));
  };

  // Cache management functions removed - using simplified thumbnail approach

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const createNewAlbum = async () => {
    if (!newAlbumName.trim() || selectedPhotosForAlbum.length === 0) return;
    
    console.log('Creating album with data:', {
      name: newAlbumName,
      photoCount: selectedPhotosForAlbum.length,
      photos: selectedPhotosForAlbum.map(p => ({ id: p.id, filename: p.filename, path: p.path }))
    });
    
    setLoading(true);
    try {
      await apiCall('/api/albums', {
        method: 'POST',
        body: JSON.stringify({
          name: newAlbumName,
          photos: selectedPhotosForAlbum,
          password: albumPassword || null,
          accessDuration: albumPassword ? accessDuration : null,
          sourceFolder: selectedFolder || ''
        })
      });
      
      setNewAlbumName('');
      setSelectedPhotosForAlbum([]);
      setAlbumPassword('');
      setCurrentView('albums');
      loadAlbums();
    } catch (err) {
      setError('Failed to create album');
    } finally {
      setLoading(false);
    }
  };

  const saveEditedAlbum = async () => {
    if (!newAlbumName.trim() || !editingAlbum) return;
    
    setLoading(true);
    try {
      await apiCall(`/api/albums/${editingAlbum.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: newAlbumName,
          photos: selectedPhotosForAlbum,
          password: albumPassword || null,
          accessDuration: albumPassword ? accessDuration : null
        })
      });
      
      setIsEditing(false);
      setEditingAlbum(null);
      setNewAlbumName('');
      setSelectedPhotosForAlbum([]);
      setAlbumPassword('');
      setCurrentView('albums');
      loadAlbums();
    } catch (err) {
      setError('Failed to update album');
    } finally {
      setLoading(false);
    }
  };

  const deleteAlbum = async (albumId) => {
    if (!confirm('Are you sure you want to delete this album?')) return;
    
    setLoading(true);
    try {
      await apiCall(`/api/albums/${albumId}`, {
        method: 'DELETE'
      });
      loadAlbums();
    } catch (err) {
      setError('Failed to delete album');
    } finally {
      setLoading(false);
    }
  };

  const updateAlbumThumbnail = async (albumId, coverPhoto) => {
    console.log('updateAlbumThumbnail called with:', { albumId, coverPhoto });
    setLoading(true);
    try {
      await apiCall(`/api/albums/${albumId}/thumbnail`, {
        method: 'PUT',
        body: JSON.stringify({ coverPhoto })
      });
      loadAlbums();
      setShowThumbnailSelector(false);
      setSelectedThumbnail(null);
    } catch (err) {
      console.error('Error updating thumbnail:', err);
      setError('Failed to update album thumbnail');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const handleAlbumAuth = async () => {
    if (!albumPassword.trim() || !albumEmail.trim() || !selectedAlbum) return;
    
    setLoading(true);
    setError('');
    
    try {
      const result = await authenticateAlbum(selectedAlbum.id, albumPassword, albumEmail);
      
      if (result.requiresVerification) {
        // Email verification required
        setVerificationSent(true);
        setVerificationEmail(albumEmail);
        setCurrentView('verification-sent');
        setAlbumPassword('');
        setAlbumEmail('');
      } else if (result.success) {
        // Use the token directly from the result instead of relying on state
        const headers = result.token ? { 'Authorization': `Bearer ${result.token}` } : {};
        
        // For shared albums, use the shared endpoint; otherwise use the regular album endpoint
        const endpoint = selectedAlbum.isShared 
          ? `/api/shared/${selectedAlbum.sharedToken}`
          : `/api/albums/${selectedAlbum.id}`;
        
        const albumData = await apiCall(endpoint, { headers });
        if (albumData) {
          setSelectedAlbum(albumData);
          setCurrentView('photos');
          setAlbumPassword('');
          setAlbumEmail('');
        }
      }
    } catch (err) {
      setError('Failed to authenticate album');
    } finally {
      setLoading(false);
    }
  };

  const handleAlbumAuthKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAlbumAuth();
    }
  };

  const handleCollectionAuth = async () => {
    if (!albumPassword.trim() || !albumEmail.trim() || !selectedCollection) return;
    
    setLoading(true);
    setError('');
    
    try {
      const result = await authenticateCollection(selectedCollection.id, albumPassword, albumEmail);
      
      if (result.requiresVerification) {
        // Email verification required
        setVerificationSent(true);
        setVerificationEmail(albumEmail);
        setCurrentView('verification-sent');
        setAlbumPassword('');
        setAlbumEmail('');
      } else if (result.success) {
        // Use the token directly from the result instead of relying on state
        const headers = result.token ? { 'Authorization': `Bearer ${result.token}` } : {};
        
        // Load the collection data and show it
        const collectionData = selectedCollection.isShared 
          ? await apiCall(`/api/shared/collection/${selectedCollection.sharedToken}`, { headers })
          : await apiCall(`/api/collections/${selectedCollection.id}`, { headers });
        
        if (collectionData) {
          setSelectedCollection(collectionData);
          setCurrentView('collection-view');
          setAlbumPassword('');
          setAlbumEmail('');
        }
      }
    } catch (err) {
      setError('Failed to authenticate collection');
    } finally {
      setLoading(false);
    }
  };

  const handleCollectionAuthKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCollectionAuth();
    }
  };

  const handleVerificationComplete = async () => {
    // After email verification, try to authenticate again
    if (selectedAlbum && verificationEmail) {
      setLoading(true);
      setError('');
      
      try {
        // Try to authenticate with the stored password (if any) or prompt user to re-enter
        if (albumPassword) {
          const result = await authenticateAlbum(selectedAlbum.id, albumPassword, verificationEmail);
          
          if (result.requiresVerification) {
            setError('Email verification still required. Please check your email.');
            return;
          } else if (result.success) {
            // Use the token directly from the result instead of relying on state
            const headers = result.token ? { 'Authorization': `Bearer ${result.token}` } : {};
            
            // For shared albums, use the shared endpoint; otherwise use the regular album endpoint
            const endpoint = selectedAlbum.isShared 
              ? `/api/shared/${selectedAlbum.sharedToken}`
              : `/api/albums/${selectedAlbum.id}`;
            
            const albumData = await apiCall(endpoint, { headers });
            if (albumData) {
              setSelectedAlbum(albumData);
              setCurrentView('photos');
              setVerificationSent(false);
              setVerificationEmail('');
              setAlbumPassword('');
              setAlbumEmail('');
            }
          }
        } else {
          // No stored password, prompt user to re-enter
          setCurrentView('album-auth');
          setAlbumEmail(verificationEmail);
          setVerificationSent(false);
          setVerificationEmail('');
          setError('Please enter the album password again to access the album.');
        }
      } catch (err) {
        setError('Failed to access album after verification');
      } finally {
        setLoading(false);
      }
    }
  };

  const openShareModal = (item, type = 'album') => {
    setShareModalAlbum({ ...item, type }); // Store both item and type
    setShareUrl('');
    setShareDuration('1440');
    setShowShareModal(true);
  };

  const handleCreateShare = async () => {
    if (!shareModalAlbum) return;
    
    setLoading(true);
    try {
      let result;
      if (shareModalAlbum.type === 'collection') {
        result = await createCollectionSharedLink(shareModalAlbum.id, shareDuration);
      } else {
        result = await createSharedLink(shareModalAlbum.id, shareDuration);
      }
      setShareUrl(result.shareUrl);
    } catch (err) {
      // Error already set in createSharedLink
    } finally {
      setLoading(false);
    }
  };

  const createCollectionSharedLink = async (collectionId, duration) => {
    try {
      const result = await apiCall(`/api/collections/${collectionId}/share`, {
        method: 'POST',
        body: JSON.stringify({ accessDuration: duration })
      });
      return result;
    } catch (err) {
      setError('Failed to create collection shared link');
      throw err;
    }
  };

  const loadCollectionSharedLinks = async (collectionId) => {
    try {
      const links = await apiCall(`/api/collections/${collectionId}/shared-links`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return links;
    } catch (err) {
      console.error('Error loading collection shared links:', err);
      return [];
    }
  };

  const deleteCollectionSharedLink = async (token) => {
    try {
      await apiCall(`/api/collections/shared-links/${token}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return true;
    } catch (err) {
      setError('Failed to delete collection shared link');
      return false;
    }
  };

  const loadCollectionVerifiedUsers = async (collectionId) => {
    try {
      const users = await apiCall(`/api/collections/${collectionId}/verified-users`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return users;
    } catch (err) {
      console.error('Error loading collection verified users:', err);
      return [];
    }
  };

  const removeCollectionVerifiedUser = async (collectionId, email) => {
    try {
      await apiCall(`/api/collections/${collectionId}/verified-users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return true;
    } catch (err) {
      setError('Failed to remove collection verified user');
      return false;
    }
  };

  const openCollectionSharedLinksModal = async (collection) => {
    setSelectedCollectionForManagement(collection);
    setLoading(true);
    try {
      const links = await loadCollectionSharedLinks(collection.id);
      setCollectionSharedLinks(links);
      setShowCollectionSharedLinksModal(true);
    } catch (err) {
      setError('Failed to load collection shared links');
    } finally {
      setLoading(false);
    }
  };

  const openCollectionVerifiedUsersModal = async (collection) => {
    setSelectedCollectionForManagement(collection);
    setLoading(true);
    try {
      const users = await loadCollectionVerifiedUsers(collection.id);
      setCollectionVerifiedUsers(users);
      setShowCollectionVerifiedUsersModal(true);
    } catch (err) {
      setError('Failed to load collection verified users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCollectionSharedLink = async (token) => {
    if (window.confirm('Are you sure you want to delete this shared link?')) {
      const success = await deleteCollectionSharedLink(token);
      if (success) {
        // Refresh the list
        const links = await loadCollectionSharedLinks(selectedCollectionForManagement.id);
        setCollectionSharedLinks(links);
      }
    }
  };

  const handleRemoveCollectionVerifiedUser = async (email) => {
    if (window.confirm(`Are you sure you want to remove ${email} from this collection?`)) {
      const success = await removeCollectionVerifiedUser(selectedCollectionForManagement.id, email);
      if (success) {
        // Refresh the list
        const users = await loadCollectionVerifiedUsers(selectedCollectionForManagement.id);
        setCollectionVerifiedUsers(users);
      }
    }
  };

  const handleCopyShareUrl = () => {
    copyToClipboard(shareUrl);
  };

  const openAlbum = async (album) => {
    console.log('Opening album:', {
      albumId: album.id,
      albumName: album.name,
      isProtected: album.isProtected,
      hasToken: !!albumAuthTokens[album.id],
      userType: userType,
      isShared: album.isShared,
      hasSharedToken: !!album.sharedToken
    });
    
    // If album is from a shared collection and has photos, open directly
    if (album.isShared && album.photos && album.photos.length > 0) {
      console.log('Album is from shared collection and has photos, opening directly');
      setSelectedAlbum(album);
      setCurrentView('photos');
      return;
    }
    
    // If album is not protected, try to load it
    if (!album.isProtected) {
      console.log('Album is not protected, loading album data');
      try {
        // If user is admin, use admin token for all album access
        const headers = userType === 'admin' && adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
        const albumData = await apiCall(`/api/albums/${album.id}`, { headers });
        setSelectedAlbum(albumData);
        setCurrentView('photos');
        return;
      } catch (err) {
        console.log('Failed to load unprotected album, might need authentication');
      }
    }
    
    // If album is protected and we have a valid token, try to load it
    if (albumAuthTokens[album.id]) {
      console.log('Album is protected but we have a token, trying to load');
      const albumData = await loadAlbumWithAuth(album.id);
      if (albumData) {
        console.log('Successfully loaded album with token');
        setSelectedAlbum(albumData);
        setCurrentView('photos');
        return;
      } else {
        console.log('Token is invalid or expired, removing it');
        // Token is invalid or expired, remove it
        setAlbumAuthTokens(prev => {
          const newTokens = { ...prev };
          delete newTokens[album.id];
          return newTokens;
        });
      }
    }
    
    // If user is admin, try to load protected album with admin token
    if (userType === 'admin' && adminToken) {
      console.log('User is admin, trying to load protected album with admin token');
      const albumData = await loadAlbumWithAuth(album.id);
      if (albumData) {
        console.log('Successfully loaded protected album with admin token');
        setSelectedAlbum(albumData);
        setCurrentView('photos');
        return;
      }
    }
    
    // Album is protected and needs authentication
    console.log('Album is protected and needs authentication');
    setSelectedAlbum(album);
    setCurrentView('album-auth');
  };

  const backToAlbums = () => {
    if (isSharedView && !selectedCollection) {
      // For directly shared albums (not part of a collection), redirect to the main gallery
      window.location.href = '/';
      return;
    }
    
    // Determine which view to go back to based on current context
    if (currentView === 'manage-collections' || currentView === 'collection-view' || selectedCollection) {
      setCurrentView(selectedCollection ? 'collection-view' : 'collections');
      if (!selectedCollection) {
        setEditingCollection(null);
        setNewCollectionName('');
        setNewCollectionDescription('');
        setSelectedAlbumsForCollection([]);
      }
      setSelectedAlbum(null);
      setSelectedPhotosForAlbum([]);
      setAlbumPassword('');
      setAlbumEmail('');
      setVerificationSent(false);
      setVerificationEmail('');
      setAccessDuration('1440');
    } else {
      setCurrentView('albums');
      setSelectedAlbum(null);
      setSelectedPhotosForAlbum([]);
      setAlbumPassword('');
      setAlbumEmail('');
      setVerificationSent(false);
      setVerificationEmail('');
      setAccessDuration('1440');
    }
    setError('');
  };

  const openManageView = () => {
    setCurrentView('manage');
  };

  const openBrowseView = () => {
    setCurrentView('browse');
    setSelectedPhotosForAlbum([]);
    setCurrentPage(1); // Reset to first page
    setSelectedFolder(''); // Reset folder selection
    setAvailablePhotos([]); // Clear existing photos - user must scan
    loadAvailableFolders(); // Load available folders
    // Don't automatically load photos - wait for user to select folder and scan
  };

  // Collections helper functions
  const openManageCollectionsView = () => {
    setCurrentView('manage-collections');
    setEditingCollection(null);
    setNewCollectionName('');
    setNewCollectionDescription('');
    setSelectedAlbumsForCollection([]);
    setAlbumPassword('');
    setAccessDuration('1440');
  };

  const openEditCollection = async (collection) => {
    setEditingCollection(collection);
    setNewCollectionName(collection.name);
    setNewCollectionDescription(collection.description || '');
    setSelectedAlbumsForCollection(collection.albums?.map(album => album.id) || []);
    
    // Load actual password for editing if collection is protected
    if (collection.password) {
      try {
        const headers = adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
        const passwordData = await apiCall(`/api/collections/${collection.id}/password`, { headers });
        setAlbumPassword(passwordData.password || '');
      } catch (err) {
        console.error('Error loading collection password for editing:', err);
        setAlbumPassword(''); // Fallback to empty if can't load
      }
    } else {
      setAlbumPassword('');
    }
    
    setAccessDuration(collection.accessDurationMinutes?.toString() || '1440');
    setCurrentView('manage-collections');
  };

  const openCollection = (collection) => {
    setSelectedCollection(collection);
    setCurrentView('collection-view');
  };

  const deleteCollection = async (collectionId) => {
    if (!window.confirm('Are you sure you want to delete this collection? This will not delete the albums inside it.')) {
      return;
    }

    try {
      await apiCall(`/api/collections/${collectionId}`, {
        method: 'DELETE'
      });
      loadCollections();
    } catch (err) {
      setError('Failed to delete collection');
    }
  };

  const toggleAlbumSelection = (albumId) => {
    setSelectedAlbumsForCollection(prev => {
      const isSelected = prev.includes(albumId);
      return isSelected
        ? prev.filter(id => id !== albumId)
        : [...prev, albumId];
    });
  };

  const saveCollection = async () => {
    if (!newCollectionName.trim()) {
      setError('Collection name is required');
      return;
    }

    setLoading(true);
    try {
      const collectionData = {
        name: newCollectionName.trim(),
        description: newCollectionDescription.trim(),
        albums: selectedAlbumsForCollection,
        password: albumPassword.trim() || null,
        accessDuration: accessDuration
      };

      if (editingCollection) {
        await apiCall(`/api/collections/${editingCollection.id}`, {
          method: 'PUT',
          body: JSON.stringify(collectionData)
        });
      } else {
        await apiCall('/api/collections', {
          method: 'POST',
          body: JSON.stringify(collectionData)
        });
      }

      setCurrentView('collections');
      setEditingCollection(null);
      setNewCollectionName('');
      setNewCollectionDescription('');
      setSelectedAlbumsForCollection([]);
      setAlbumPassword('');
      setAccessDuration('1440');
      loadCollections();
    } catch (err) {
      setError(editingCollection ? 'Failed to update collection' : 'Failed to create collection');
    } finally {
      setLoading(false);
    }
  };

  const startEditingAlbum = async (album) => {
    setEditingAlbum(album);
    setNewAlbumName(album.name);
    setSelectedPhotosForAlbum([...album.photos]);
    
    // Load actual password for editing if album is protected
    if (album.password) {
      try {
        const headers = adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
        const passwordData = await apiCall(`/api/albums/${album.id}/password`, { headers });
        setAlbumPassword(passwordData.password || '');
      } catch (err) {
        console.error('Error loading album password for editing:', err);
        setAlbumPassword(''); // Fallback to empty if can't load
      }
    } else {
      setAlbumPassword('');
    }
    
    setAccessDuration(album.accessDurationMinutes?.toString() || '1440');
    setIsEditing(true);
    setCurrentView('browse');
    setCurrentPage(1); // Reset to first page
    // Use the album's original source folder, or empty for legacy albums
    const albumSourceFolder = album.source_folder || '';
    setSelectedFolder(albumSourceFolder);
    loadAvailableFolders(); // Load available folders
    // For editing, load photos from the album's original source folder
    loadAvailablePhotosProgressive(albumSourceFolder);
  };

  const togglePhotoSelection = (photo) => {
    console.log('togglePhotoSelection called with:', {
      photoId: photo.id,
      photoName: photo.filename,
      currentSelectionCount: selectedPhotosForAlbum.length,
      currentSelectionIds: selectedPhotosForAlbum.map(p => p.id)
    });
    
    setSelectedPhotosForAlbum(prev => {
      const isSelected = prev.find(p => p.id === photo.id);
      const newSelection = isSelected
        ? prev.filter(p => p.id !== photo.id)
        : [...prev, photo];
      
      console.log('Photo selection toggled:', {
        photoId: photo.id,
        photoName: photo.filename,
        wasSelected: isSelected,
        newSelectionCount: newSelection.length,
        newSelectionIds: newSelection.map(p => p.id),
        allPhotoIds: availablePhotos.map(p => p.id)
      });
      
      return newSelection;
    });
  };

  const isPhotoSelected = (photo) => {
    const selected = selectedPhotosForAlbum.some(p => p.id === photo.id);
    console.log('isPhotoSelected check:', {
      photoId: photo.id,
      photoName: photo.filename,
      isSelected: selected,
      selectedPhotosCount: selectedPhotosForAlbum.length
    });
    return selected;
  };

  const getSortedPhotos = (photos) => {
    if (!photos) return [];
    
    const sortedPhotos = [...photos];
    switch (sortOrder) {
      case 'newest':
        return sortedPhotos.sort((a, b) => {
          const dateA = new Date(a.created || a.modified);
          const dateB = new Date(b.created || b.modified);
          return dateB - dateA;
        });
      case 'oldest':
      default:
        return sortedPhotos.sort((a, b) => {
          const dateA = new Date(a.created || a.modified);
          const dateB = new Date(b.created || b.modified);
          return dateA - dateB;
        });
      case 'name':
        return sortedPhotos.sort((a, b) => a.filename.localeCompare(b.filename));
    }
  };

  const getSortedAlbumPhotos = (photos) => {
    if (!photos) return [];
    
    const sortedPhotos = [...photos];
    switch (albumPhotosSortOrder) {
      case 'newest':
        return sortedPhotos.sort((a, b) => {
          const dateA = new Date(a.created || a.modified);
          const dateB = new Date(b.created || b.modified);
          return dateB - dateA;
        });
      case 'oldest':
      default:
        return sortedPhotos.sort((a, b) => {
          const dateA = new Date(a.created || a.modified);
          const dateB = new Date(b.created || b.modified);
          return dateA - dateB;
        });
      case 'name':
        return sortedPhotos.sort((a, b) => a.filename.localeCompare(b.filename));
    }
  };

  const getPaginatedPhotos = (photos) => {
    const sortedPhotos = getSortedPhotos(photos);
    const startIndex = (currentPage - 1) * photosPerPage;
    const endIndex = startIndex + photosPerPage;
    return sortedPhotos.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(totalPhotos / photosPerPage);
  };

  const getCurrentPageRange = () => {
    const startIndex = (currentPage - 1) * photosPerPage + 1;
    const endIndex = Math.min(currentPage * photosPerPage, totalPhotos);
    return { startIndex, endIndex };
  };

  const getPageNumbers = () => {
    const totalPages = getTotalPages();
    const current = currentPage;
    const pages = [];
    
    // Always show first page
    pages.push(1);
    
    // Show pages around current page
    const start = Math.max(2, current - 2);
    const end = Math.min(totalPages - 1, current + 2);
    
    if (start > 2) {
      pages.push('...');
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    if (end < totalPages - 1) {
      pages.push('...');
    }
    
    // Always show last page if there's more than one page
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  const handleJumpToPage = () => {
    const pageNumber = parseInt(jumpToPage);
    const totalPages = getTotalPages();
    
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setJumpToPage(''); // Clear the input after jumping
    } else {
      // You could add an error message here
      console.log(`Invalid page number: ${pageNumber}. Must be between 1 and ${totalPages}`);
    }
  };

  const handleJumpToPageKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleJumpToPage();
    }
  };

  const openPhoto = (photo) => {
    setSelectedPhoto(photo);
  };

  const closePhoto = () => {
    setSelectedPhoto(null);
  };

  const nextPhoto = () => {
    if (!selectedAlbum || !selectedPhoto) return;
    const currentIndex = selectedAlbum.photos.findIndex(p => p.id === selectedPhoto.id);
    const nextIndex = (currentIndex + 1) % selectedAlbum.photos.length;
    setSelectedPhoto(selectedAlbum.photos[nextIndex]);
  };

  const prevPhoto = () => {
    if (!selectedAlbum || !selectedPhoto) return;
    const currentIndex = selectedAlbum.photos.findIndex(p => p.id === selectedPhoto.id);
    const prevIndex = currentIndex === 0 ? selectedAlbum.photos.length - 1 : currentIndex - 1;
    setSelectedPhoto(selectedAlbum.photos[prevIndex]);
  };

  // Video link management functions
  const openVideoLinkModal = (album) => {
    setVideoLinkAlbum(album);
    // Always set the URL to the current album's video link, even if it's null/empty
    setVideoLinkUrl(album.videoLink || '');
    setShowVideoLinkModal(true);
  };

  const saveVideoLink = async () => {
    if (!videoLinkAlbum) return;
    
    setLoading(true);
    try {
      const trimmedUrl = videoLinkUrl.trim();
      
      await apiCall(`/api/albums/${videoLinkAlbum.id}/video-link`, {
        method: 'PUT',
        body: JSON.stringify({ videoLink: trimmedUrl })
      });
      
      // Reload albums to get fresh data from server instead of relying on local state update
      await loadAlbums();
      
      // Close modal and clear state only after successful save
      setShowVideoLinkModal(false);
      setVideoLinkAlbum(null);
      setVideoLinkUrl('');
    } catch (err) {
      setError('Failed to save video link: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const deleteVideoLink = async () => {
    if (!videoLinkAlbum) return;
    
    setLoading(true);
    try {
      await apiCall(`/api/albums/${videoLinkAlbum.id}/video-link`, {
        method: 'DELETE'
      });
      
      // Reload albums to get fresh data from server
      await loadAlbums();
      
      setShowVideoLinkModal(false);
      setVideoLinkAlbum(null);
      setVideoLinkUrl('');
    } catch (err) {
      setError('Failed to delete video link');
    } finally {
      setLoading(false);
    }
  };

  // Access logs management functions
  const openLogsModal = async (album) => {
    setLogsAlbum(album);
    setShowLogsModal(true);
    
    // Load access logs for the album
    setLoading(true);
    try {
      const headers = adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
      const logs = await apiCall(`/api/albums/${album.id}/access-logs`, {
        headers
      });
      setAccessLogs(logs);
    } catch (err) {
      setError('Failed to load access logs');
    } finally {
      setLoading(false);
    }
  };

  const closeLogsModal = () => {
    setShowLogsModal(false);
    setLogsAlbum(null);
    setAccessLogs([]);
  };

  const closeVideoLinkModal = () => {
    setShowVideoLinkModal(false);
    setVideoLinkAlbum(null);
    // Don't clear videoLinkUrl here - let it persist until the next modal opens
  };

  useEffect(() => {
    const handleKeyboardNav = (e) => {
      if (!selectedPhoto) return;
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'ArrowLeft') prevPhoto();
      if (e.key === 'Escape') closePhoto();
    };

    window.addEventListener('keydown', handleKeyboardNav);
    return () => window.removeEventListener('keydown', handleKeyboardNav);
  }, [selectedPhoto, selectedAlbum]);

  // Reset pagination when sort order changes
  useEffect(() => {
    if (currentView === 'browse') {
      setCurrentPage(1);
    }
  }, [sortOrder, currentView]);

  // Keyboard shortcuts for pagination
  useEffect(() => {
    const handlePaginationKeyPress = (e) => {
      if (currentView !== 'browse') return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setCurrentPage(prev => Math.max(1, prev - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setCurrentPage(prev => Math.min(getTotalPages(), prev + 1));
          break;
        case 'Home':
          e.preventDefault();
          setCurrentPage(1);
          break;
        case 'End':
          e.preventDefault();
          setCurrentPage(getTotalPages());
          break;
      }
    };

    window.addEventListener('keydown', handlePaginationKeyPress);
    return () => window.removeEventListener('keydown', handlePaginationKeyPress);
  }, [currentView, getTotalPages]);

  // Handle shared links on page load
  useEffect(() => {
    const path = window.location.pathname;
    const sharedAlbumMatch = path.match(/^\/shared\/([a-f0-9]+)$/);
    const sharedCollectionMatch = path.match(/^\/shared\/collection\/([a-f0-9]+)$/);
    
    if (sharedAlbumMatch) {
      const token = sharedAlbumMatch[1];
      setSharedToken(token);
      setIsSharedView(true);
      
      loadSharedAlbum(token).then(albumData => {
        if (albumData) {
          setSelectedAlbum(albumData);
          // If the shared album is protected, show the authentication page
          if (albumData.isProtected) {
            setCurrentView('album-auth');
            // Show a success message if they came from email verification
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('verified') === 'true') {
              setError(''); // Clear any existing errors
              // You could add a success message here if needed
            }
          } else {
            setCurrentView('photos');
          }
        }
      }).catch(err => {
        console.error('Failed to load shared album:', err);
        setError('Shared link not found or expired');
      });
    } else if (sharedCollectionMatch) {
      const token = sharedCollectionMatch[1];
      setSharedToken(token);
      setIsSharedView(true);
      
      loadSharedCollection(token).then(collectionData => {
        if (collectionData) {
          setSelectedCollection(collectionData);
          // If the shared collection is protected, show the authentication page
          if (collectionData.isProtected) {
            setCurrentView('collection-auth');
            // Show a success message if they came from email verification
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('verified') === 'true') {
              setError(''); // Clear any existing errors
            }
          } else {
            setCurrentView('collection-view');
          }
        }
      }).catch(err => {
        console.error('Failed to load shared collection:', err);
        setError('Shared collection link not found or expired');
      });
    }
  }, []);

  if (!isAuthenticated && !isSharedView) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Photo Gallery</h1>
            <p className="text-gray-600">Enter password to view albums</p>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-12"
                autoFocus
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Lock className="w-5 h-5" />
              {loading ? 'Authenticating...' : 'Access Gallery'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {(currentView === 'photos' || currentView === 'manage' || currentView === 'browse' || currentView === 'verification-sent' || currentView === 'manage-collections' || currentView === 'collection-view' || currentView === 'album-auth' || currentView === 'collection-auth') && (
                <button
                  onClick={backToAlbums}
                  className="p-2 hover:bg-gray-100 rounded-lg transition duration-200"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div className="flex items-center gap-2">
                <Camera className="w-6 h-6 text-indigo-600" />
                <h1 className="text-xl font-semibold text-gray-900">
                  {currentView === 'albums' && 'Photo Albums'}
                  {currentView === 'collections' && 'Photo Collections'}
                  {currentView === 'collection-view' && (selectedCollection?.isShared ? `Shared: ${selectedCollection?.name}` : selectedCollection?.name)}
                  {currentView === 'collection-auth' && `Enter Password: ${selectedCollection?.isShared ? 'Shared: ' : ''}${selectedCollection?.name}`}
                  {currentView === 'photos' && (selectedAlbum?.isShared ? `Shared: ${selectedAlbum?.name}` : selectedAlbum?.name)}
                  {currentView === 'album-auth' && `Enter Password: ${selectedAlbum?.isShared ? 'Shared: ' : ''}${selectedAlbum?.name}`}
                  {currentView === 'verification-sent' && 'Email Verification'}
                  {currentView === 'manage' && 'Manage Albums'}
                  {currentView === 'album-photos' && `Photos: ${selectedAlbum?.name}`}
                  {currentView === 'browse' && (isEditing ? `Edit: ${editingAlbum?.name}` : 'Create New Album')}
                  {currentView === 'manage-collections' && (editingCollection ? `Edit: ${editingCollection?.name}` : 'Create New Collection')}
                </h1>
                {userType && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    userType === 'admin' 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {userType === 'admin' ? 'Admin' : 'User'}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {currentView === 'album-photos' && (
                <button
                  onClick={() => setCurrentView('manage')}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition duration-200 flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Manage
                </button>
              )}
              {currentView === 'albums' && userType === 'admin' && (
                <>
                  <button
                    onClick={openManageView}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Manage Albums
                  </button>
                  <button
                    onClick={openSharedLinkManager}
                    className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition duration-200 flex items-center gap-2"
                  >
                    <Share2 className="w-4 h-4" />
                    Shared Links
                  </button>
                  <button
                    onClick={openPasswordManager}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition duration-200 flex items-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    View Passwords
                  </button>
                </>
              )}
              {currentView === 'collections' && userType === 'admin' && (
                <>
                  <button
                    onClick={openManageCollectionsView}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Manage Collections
                  </button>
                  <button
                    onClick={openCollectionPasswordManager}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition duration-200 flex items-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    View Passwords
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setIsAuthenticated(false);
                  setUserType(null);
                  setAdminToken(null);
                  setAlbumAuthTokens({}); // Clear album authentication tokens
                }}
                className="text-gray-600 hover:text-gray-900 text-sm px-3 py-2 rounded hover:bg-gray-100"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Loading/Error Display */}
      {loading && (
        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4">
          Loading...
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
          {error}
          <button 
            onClick={() => setError('')}
            className="ml-4 text-red-900 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      {isAuthenticated && (currentView === 'albums' || currentView === 'collections') && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex space-x-8">
              <button
                onClick={() => setCurrentView('albums')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  currentView === 'albums'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Albums
              </button>
              <button
                onClick={() => setCurrentView('collections')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  currentView === 'collections'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Collections
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Albums View */}
      {currentView === 'albums' && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          {albums.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No albums yet</h3>
              {userType === 'admin' && (
                <>
                  <p className="text-gray-600 mb-6">Create your first album to get started</p>
                  <button
                    onClick={openManageView}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition duration-200"
                  >
                    Create Album
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {albums.map(album => (
                <div
                  key={album.id}
                  onClick={() => openAlbum(album)}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition duration-300 cursor-pointer overflow-hidden group"
                >
                  <div className="aspect-video overflow-hidden bg-gray-200 relative">
                    {album.coverPhoto && (
                      <img
                        src={album.coverPhoto}
                        alt={album.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {album.isProtected && (
                      <div className="absolute top-3 right-3 bg-black bg-opacity-50 rounded-full p-2">
                        <Shield className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-3 left-3 bg-black bg-opacity-50 rounded-full px-2 py-1">
                      <span className="text-white text-sm">
                        {album.photo_count} {album.photo_count === 1 ? 'photo' : 'photos'}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900">{album.name}</h3>
                      <div className="flex items-center gap-2">
                        {userType === 'admin' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openShareModal(album);
                            }}
                            className="p-1 hover:bg-gray-100 rounded transition duration-200"
                            title="Share album"
                          >
                            <Share2 className="w-4 h-4 text-gray-600" />
                          </button>
                        )}
                      </div>
                    </div>
                    {album.isProtected && album.accessDurationMinutes && (
                      <p className="text-xs text-gray-500">
                        Access: {album.accessDurationMinutes === -1 ? (
                          '∞'
                        ) : album.accessDurationMinutes >= 60 
                          ? album.accessDurationMinutes >= 1440 
                            ? album.accessDurationMinutes >= 43200
                              ? album.accessDurationMinutes >= 129600
                                ? album.accessDurationMinutes >= 259200
                                  ? album.accessDurationMinutes >= 525600
                                    ? `${Math.round(album.accessDurationMinutes / 525600)}y`
                                    : `${Math.round(album.accessDurationMinutes / 259200)}mo`
                                  : `${Math.round(album.accessDurationMinutes / 129600)}mo`
                                : `${Math.round(album.accessDurationMinutes / 43200)}mo`
                              : `${Math.round(album.accessDurationMinutes / 1440)}d`
                            : `${Math.round(album.accessDurationMinutes / 60)}h`
                          : `${album.accessDurationMinutes}m`
                        }
                      </p>
                    )}
                    {album.videoLink && (
                      <div className="mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(album.videoLink, '_blank');
                          }}
                          className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs hover:bg-red-200 transition duration-200 flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Videos
                        </button>
                      </div>
                    )}
                    {userType === 'admin' && (
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingAlbum(album);
                          }}
                          className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                        >
                          <Edit3 className="w-3 h-3" />
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openShareModal(album);
                          }}
                          className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"
                        >
                          <Share2 className="w-3 h-3" />
                          Share
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openSharedLinkManager();
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Links
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openVideoLinkModal(album);
                          }}
                          className="text-pink-600 hover:text-pink-800 text-sm flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Videos
                        </button>
                        {album.isProtected && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openUserManager(album);
                              }}
                              className="text-purple-600 hover:text-purple-800 text-sm flex items-center gap-1"
                            >
                              <Mail className="w-3 h-3" />
                              Users
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openLogsModal(album);
                              }}
                              className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Logs
                            </button>
                          </>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteAlbum(album.id);
                          }}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Manage Albums View */}
      {currentView === 'manage' && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Manage Albums</h2>
            <button
              onClick={openBrowseView}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition duration-200 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create New Album
            </button>
          </div>
          
          {albums.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No albums yet</h3>
              <p className="text-gray-600 mb-6">Create your first album to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {albums.map(album => (
                <div
                  key={album.id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition duration-300 overflow-hidden"
                >
                  <div className="aspect-video overflow-hidden bg-gray-200">
                    {album.coverPhoto && (
                      <img
                        src={album.coverPhoto}
                        alt={album.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900">{album.name}</h3>
                      <div className="flex items-center gap-2">
                        {album.isProtected && (
                          <Shield className="w-4 h-4 text-indigo-600" />
                        )}
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-1">{album.photo_count} photos</p>
                    <p className="text-gray-500 text-xs mb-3">
                      Created {new Date(album.created_date).toLocaleDateString()}
                    </p>
                    
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => openAlbum(album)}
                        className="bg-indigo-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-indigo-700 transition duration-200 flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        View
                      </button>
                      <button
                        onClick={() => startEditingAlbum(album)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => openShareModal(album)}
                        className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"
                      >
                        <Share2 className="w-3 h-3" />
                        Share
                      </button>
                      {album.isProtected && (
                        <>
                          <button
                            onClick={() => openUserManager(album)}
                            className="text-purple-600 hover:text-purple-800 text-sm flex items-center gap-1"
                          >
                            <Mail className="w-3 h-3" />
                            Users
                          </button>
                          <button
                            onClick={() => openLogsModal(album)}
                            className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Logs
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => deleteAlbum(album.id)}
                        className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Collections View */}
      {currentView === 'collections' && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          {collections.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No collections yet</h3>
              {userType === 'admin' && (
                <>
                  <p className="text-gray-600 mb-6">Create your first collection to get started</p>
                  <button
                    onClick={openManageCollectionsView}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition duration-200"
                  >
                    Create Collection
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {collections.map(collection => (
                <div
                  key={collection.id}
                  onClick={() => openCollection(collection)}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition duration-300 cursor-pointer overflow-hidden group"
                >
                  <div className="relative">
                    <img
                      src={collection.coverPhoto}
                      alt={collection.name}
                      className="w-full h-48 object-cover group-hover:scale-105 transition duration-300"
                      onError={(e) => {
                        e.target.src = '/api/placeholder.jpg';
                      }}
                    />
                    {collection.isProtected && (
                      <div className="absolute top-3 right-3 bg-black bg-opacity-50 rounded-full p-2">
                        <Lock className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-3 left-3 bg-black bg-opacity-50 rounded-full px-2 py-1">
                      <span className="text-white text-sm">
                        {collection.album_count} {collection.album_count === 1 ? 'album' : 'albums'}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">{collection.name}</h3>
                    {collection.description && (
                      <p className="text-gray-600 text-sm mb-2">{collection.description}</p>
                    )}
                    <p className="text-gray-500 text-sm">
                      Created {new Date(collection.created_date).toLocaleDateString()}
                    </p>
                    {userType === 'admin' && (
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditCollection(collection);
                          }}
                          className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                        >
                          <Edit3 className="w-3 h-3" />
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openShareModal(collection, 'collection');
                          }}
                          className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"
                        >
                          <Share2 className="w-3 h-3" />
                          Share
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openCollectionSharedLinksModal(collection);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Links
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openCollectionVerifiedUsersModal(collection);
                          }}
                          className="text-purple-600 hover:text-purple-800 text-sm flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" />
                          Users
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCollection(collection.id);
                          }}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Manage Collections View */}
      {currentView === 'manage-collections' && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-6 bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold mb-4">
              {editingCollection ? `Edit Collection: ${editingCollection.name}` : 'Create New Collection'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Collection Name *
                </label>
                <input
                  type="text"
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter collection name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={newCollectionDescription}
                  onChange={(e) => setNewCollectionDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter collection description (optional)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Collection Password
                </label>
                <div className="relative">
                  <input
                    type={showAlbumPassword ? "text" : "password"}
                    value={albumPassword}
                    onChange={(e) => setAlbumPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-10"
                    placeholder="Enter password (optional)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAlbumPassword(!showAlbumPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showAlbumPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Leave empty for public collection</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Duration
                </label>
                <select
                  value={accessDuration}
                  onChange={(e) => setAccessDuration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="60">1 hour</option>
                  <option value="360">6 hours</option>
                  <option value="720">12 hours</option>
                  <option value="1440">24 hours</option>
                  <option value="4320">3 days</option>
                  <option value="10080">1 week</option>
                  <option value="43200">1 month</option>
                  <option value="-1">Unlimited</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">How long users can access after authentication</p>
              </div>
            </div>
            
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Albums for Collection
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {albums.map(album => (
                  <div
                    key={album.id}
                    onClick={() => toggleAlbumSelection(album.id)}
                    className={`cursor-pointer rounded-lg border-2 transition duration-200 ${
                      selectedAlbumsForCollection.includes(album.id)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="p-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          selectedAlbumsForCollection.includes(album.id)
                            ? 'border-indigo-500 bg-indigo-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedAlbumsForCollection.includes(album.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{album.name}</h4>
                          <p className="text-sm text-gray-500">{album.photo_count} photos</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={saveCollection}
                disabled={!newCollectionName.trim()}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                {editingCollection ? 'Update Collection' : 'Create Collection'}
              </button>
              <button
                onClick={() => setCurrentView('collections')}
                className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition duration-200 flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection View - Shows albums within a collection */}
      {currentView === 'collection-view' && selectedCollection && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedCollection.name}</h2>
            {selectedCollection.description && (
              <p className="text-gray-600 mb-4">{selectedCollection.description}</p>
            )}
            <p className="text-sm text-gray-500">
              {selectedCollection.albums?.length || 0} albums in this collection
            </p>
          </div>

          {selectedCollection.albums && selectedCollection.albums.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {selectedCollection.albums.map(album => (
                <div
                  key={album.id}
                  onClick={() => {
                    // For collection albums, use the album data directly from the collection
                    // and mark it as coming from a shared collection if applicable
                    const albumToOpen = {
                      ...album,
                      isShared: selectedCollection.isShared,
                      sharedToken: selectedCollection.sharedToken,
                      isProtected: !!album.password // Albums might have their own protection
                    };
                    openAlbum(albumToOpen);
                  }}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition duration-300 cursor-pointer overflow-hidden group"
                >
                  <div className="relative">
                    <img
                      src={album.cover_photo || '/api/placeholder.jpg'}
                      alt={album.name}
                      className="w-full h-48 object-cover group-hover:scale-105 transition duration-300"
                      onError={(e) => {
                        e.target.src = '/api/placeholder.jpg';
                      }}
                    />
                    <div className="absolute bottom-3 left-3 bg-black bg-opacity-50 rounded-full px-2 py-1">
                      <span className="text-white text-sm">
                        {album.photo_count || 0} photos
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">{album.name}</h3>
                    <p className="text-gray-500 text-sm">
                      Created {new Date(album.created_date).toLocaleDateString()}
                    </p>
                    {album.videoLink && (
                      <div className="mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(album.videoLink, '_blank');
                          }}
                          className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs hover:bg-red-200 transition duration-200 flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Videos
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No albums in this collection</h3>
              <p className="text-gray-600">This collection is empty.</p>
            </div>
          )}
        </div>
      )}

      {/* Browse Photos View */}
      {currentView === 'browse' && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
            {!isEditing && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Select Folder to Scan</h3>
                <div className="flex items-center gap-3">
                  <select
                    value={selectedFolder}
                    onChange={(e) => {
                      setSelectedFolder(e.target.value);
                      console.log('Selected folder:', e.target.value);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">All Photos</option>
                    {availableFolders.map((folder) => {
                      const depth = (folder.path.match(/\//g) || []).length;
                      const indentPrefix = '└─ '.repeat(depth > 0 ? 1 : 0) + '  '.repeat(Math.max(0, depth - 1));
                      return (
                        <option key={folder.path} value={folder.path}>
                          {indentPrefix}{folder.path}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    onClick={() => {
                      console.log('Loading photos for folder:', selectedFolder);
                      loadAvailablePhotosProgressive(selectedFolder, true); // Force reload when explicitly clicked
                    }}
                    disabled={isScanning}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isScanning ? 'Scanning...' : 'Scan Folder'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {selectedFolder 
                    ? `Will scan: ${selectedFolder}` 
                    : 'Select a specific folder to scan for photos, or choose "All Photos" to scan everything.'}
                </p>
              </div>
            )}
            
            {/* Scanning Progress Bar */}
            {isScanning && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">{scanStatus}</span>
                  <span className="text-sm text-blue-600">{Math.round(scanProgress)}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${scanProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-4 mb-4">
              <input
                type="text"
                value={newAlbumName}
                onChange={(e) => setNewAlbumName(e.target.value)}
                placeholder="Enter album name"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <button
                onClick={(e) => {
                  e.preventDefault();
                  console.log('Create/Save button clicked:', { isEditing, newAlbumName, selectedPhotosCount: selectedPhotosForAlbum.length });
                  if (isEditing) {
                    saveEditedAlbum();
                  } else {
                    createNewAlbum();
                  }
                }}
                disabled={!newAlbumName.trim() || selectedPhotosForAlbum.length === 0 || loading}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-5 h-5" />
                {isEditing ? 'Save Changes' : 'Create Album'}
              </button>
              {isEditing && (
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditingAlbum(null);
                    setNewAlbumName('');
                    setSelectedPhotosForAlbum([]);
                    setAlbumPassword('');
                    setAlbumEmail('');
                    setCurrentView('albums');
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1">
                <input
                  type={showAlbumPassword ? "text" : "password"}
                  value={albumPassword}
                  onChange={(e) => setAlbumPassword(e.target.value)}
                  placeholder="Album password (optional)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-12"
                />
                <button
                  onClick={() => setShowAlbumPassword(!showAlbumPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showAlbumPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Shield className="w-4 h-4" />
                <span>Password Protection</span>
              </div>
            </div>
            
            {albumPassword && (
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                  <select
                    value={accessDuration}
                    onChange={(e) => setAccessDuration(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="180">3 hours</option>
                    <option value="360">6 hours</option>
                    <option value="720">12 hours</option>
                    <option value="1440">24 hours</option>
                    <option value="2880">48 hours</option>
                    <option value="10080">1 week</option>
                    <option value="43200">1 month</option>
                    <option value="129600">3 months</option>
                    <option value="259200">6 months</option>
                    <option value="525600">1 year</option>
                    <option value="-1">Unlimited</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Lock className="w-4 h-4" />
                  <span>Access Duration</span>
                </div>
              </div>
            )}
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-600">
                Selected: {selectedPhotosForAlbum.length} photos
              </p>
              {totalPhotos > photosPerPage && (
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {getTotalPages()} ({totalPhotos} photos total)
                </p>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="oldest">Oldest First</option>
                  <option value="newest">Newest First</option>
                  <option value="name">Name</option>
                </select>
              </div>
              <button
                onClick={() => {
                  console.log('Clearing selection');
                  setSelectedPhotosForAlbum([]);
                }}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Clear Selection
              </button>
              <button
                onClick={async () => {
                  console.log('Clearing backend cache');
                  try {
                    await apiCall('/api/clear-cache', { method: 'POST' });
                    loadAvailablePhotosProgressive();
                  } catch (err) {
                    console.error('Failed to clear cache:', err);
                  }
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear Cache
              </button>

              <button
                onClick={() => setCompactView(!compactView)}
                className={`text-sm px-2 py-1 rounded ${
                  compactView 
                    ? 'bg-indigo-600 text-white' 
                    : 'text-gray-600 hover:text-gray-800 border border-gray-300'
                }`}
                title={compactView ? 'Switch to normal view' : 'Switch to compact view'}
              >
                {compactView ? 'Normal View' : 'Compact View'}
              </button>
            </div>
          </div>
          
          {availablePhotos.length === 0 && !loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">
                {isEditing 
                  ? "No photos found. Check your NAS photo directory." 
                  : "Select a folder above and click 'Scan Folder' to load photos for your album."}
              </p>
            </div>
          ) : (
            <div className={`grid gap-2 ${
              compactView 
                ? 'grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12' 
                : 'grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8'
            }`}>
              {getPaginatedPhotos(availablePhotos).map((photo, index) => (
                <div
                  key={photo.id}
                  data-photo-id={photo.id}
                  data-photo-index={index}
                  onClick={() => {
                    console.log('Photo clicked:', { index, photoId: photo.id, photoName: photo.filename });
                    togglePhotoSelection(photo);
                  }}
                  className={`aspect-square overflow-hidden rounded-lg cursor-pointer group relative ${
                    isPhotoSelected(photo) ? 'ring-2 ring-indigo-500' : ''
                  }`}
                >
                  <img
                    src={photo.thumb}
                    alt={photo.filename}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm9yPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                  {isPhotoSelected(photo) && (
                    <>
                      <div className="absolute top-1 right-1 bg-indigo-600 text-white rounded-full w-4 h-4 flex items-center justify-center">
                        <Check className="w-3 h-3" />
                      </div>
                      {(() => {
                        const selectedPhoto = selectedPhotosForAlbum.find(p => p.id === photo.id);
                        if (selectedPhoto) {
                          let positionNumber;
                          if (editingAlbum && selectedPhoto.position !== undefined) {
                            // Editing existing album - show database position
                            positionNumber = selectedPhoto.position + 1;
                          } else {
                            // Creating new album - show selection order
                            positionNumber = selectedPhotosForAlbum.findIndex(p => p.id === photo.id) + 1;
                          }
                          return (
                            <div className="absolute top-1 left-1 bg-green-600 text-white rounded text-xs px-1 py-0.5 font-semibold">
                              #{positionNumber}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}
                  {!compactView && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-1">
                      <p className="text-white text-xs truncate">{photo.filename}</p>
                      {photo.exifDisplay && (
                        <p className="text-gray-300 text-xs truncate">{photo.exifDisplay}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination Controls */}
          {totalPhotos > photosPerPage && (
            <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {getTotalPages()}
                </span>
                <span className="text-sm text-gray-500">
                  (Photos {getCurrentPageRange().startIndex}-{getCurrentPageRange().endIndex} of {totalPhotos})
                </span>
              </div>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, index) => (
                  <div key={index}>
                    {page === '...' ? (
                      <span className="px-2 py-1 text-sm text-gray-400">...</span>
                    ) : (
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-2 py-1 text-sm rounded ${
                          currentPage === page
                            ? 'bg-indigo-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Page Jump Controls */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Jump to:</span>
                <input
                  type="number"
                  value={jumpToPage}
                  onChange={(e) => setJumpToPage(e.target.value)}
                  onKeyPress={handleJumpToPageKeyPress}
                  min="1"
                  max={getTotalPages()}
                  placeholder="Page #"
                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  onClick={handleJumpToPage}
                  disabled={!jumpToPage || parseInt(jumpToPage) < 1 || parseInt(jumpToPage) > getTotalPages()}
                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Go
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.min(getTotalPages(), prev + 1))}
                  disabled={currentPage === getTotalPages()}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(getTotalPages())}
                  disabled={currentPage === getTotalPages()}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <select
                  value={photosPerPage}
                  onChange={(e) => {
                    setPhotosPerPage(parseInt(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={300}>300</option>
                  <option value={500}>500</option>
                </select>
                <span className="text-sm text-gray-600">per page</span>
              </div>
              
              {/* Keyboard Shortcuts Info */}
              <div className="w-full text-center mt-2">
                <span className="text-xs text-gray-500">
                  Keyboard shortcuts: ← → (previous/next), Home (first), End (last)
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Album Authentication View */}
      {currentView === 'album-auth' && selectedAlbum && (
        <div className="max-w-md mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {selectedAlbum.isShared ? 'Shared Protected Album' : 'Protected Album'}
              </h2>
              <p className="text-gray-600 mb-2">{selectedAlbum.name}</p>
              {selectedAlbum.accessDurationMinutes && (
                <p className="text-sm text-gray-500">
                  {selectedAlbum.accessDurationMinutes === -1 ? (
                    'Access granted indefinitely'
                  ) : (
                    `Access granted for ${selectedAlbum.accessDurationMinutes >= 60 
                      ? selectedAlbum.accessDurationMinutes >= 1440 
                        ? selectedAlbum.accessDurationMinutes >= 43200
                          ? selectedAlbum.accessDurationMinutes >= 129600
                            ? selectedAlbum.accessDurationMinutes >= 259200
                              ? selectedAlbum.accessDurationMinutes >= 525600
                                ? `${Math.round(selectedAlbum.accessDurationMinutes / 525600)} year${Math.round(selectedAlbum.accessDurationMinutes / 525600) > 1 ? 's' : ''}`
                                : `${Math.round(selectedAlbum.accessDurationMinutes / 259200)} month${Math.round(selectedAlbum.accessDurationMinutes / 259200) > 1 ? 's' : ''}`
                              : `${Math.round(selectedAlbum.accessDurationMinutes / 129600)} month${Math.round(selectedAlbum.accessDurationMinutes / 129600) > 1 ? 's' : ''}`
                            : `${Math.round(selectedAlbum.accessDurationMinutes / 43200)} month${Math.round(selectedAlbum.accessDurationMinutes / 43200) > 1 ? 's' : ''}`
                          : `${Math.round(selectedAlbum.accessDurationMinutes / 1440)} day${Math.round(selectedAlbum.accessDurationMinutes / 1440) > 1 ? 's' : ''}`
                        : `${Math.round(selectedAlbum.accessDurationMinutes / 60)} hour${Math.round(selectedAlbum.accessDurationMinutes / 60) > 1 ? 's' : ''}`
                      : `${selectedAlbum.accessDurationMinutes} minute${selectedAlbum.accessDurationMinutes > 1 ? 's' : ''}`
                    }`
                  )}
                </p>
              )}
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <input
                  type="email"
                  value={albumEmail}
                  onChange={(e) => setAlbumEmail(e.target.value)}
                  onKeyPress={handleAlbumAuthKeyPress}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              
              <div className="relative">
                <input
                  type={showAlbumPassword ? "text" : "password"}
                  value={albumPassword}
                  onChange={(e) => setAlbumPassword(e.target.value)}
                  onKeyPress={handleAlbumAuthKeyPress}
                  placeholder="Enter album password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-12"
                />
                <button
                  onClick={() => setShowAlbumPassword(!showAlbumPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showAlbumPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              <button
                onClick={handleAlbumAuth}
                disabled={loading || !albumEmail.trim() || !albumPassword.trim()}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <ShieldCheck className="w-5 h-5" />
                {loading ? 'Authenticating...' : 'Access Album'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection Authentication View */}
      {currentView === 'collection-auth' && selectedCollection && (
        <div className="max-w-md mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {selectedCollection.isShared ? 'Shared Protected Collection' : 'Protected Collection'}
              </h2>
              <p className="text-gray-600 mb-2">{selectedCollection.name}</p>
              {selectedCollection.description && (
                <p className="text-sm text-gray-500 mb-2">{selectedCollection.description}</p>
              )}
              {selectedCollection.accessDurationMinutes && (
                <p className="text-sm text-gray-500">
                  {selectedCollection.accessDurationMinutes === -1 ? (
                    'Access granted indefinitely'
                  ) : (
                    `Access granted for ${selectedCollection.accessDurationMinutes >= 60 
                      ? selectedCollection.accessDurationMinutes >= 1440 
                        ? selectedCollection.accessDurationMinutes >= 43200
                          ? selectedCollection.accessDurationMinutes >= 129600
                            ? selectedCollection.accessDurationMinutes >= 259200
                              ? selectedCollection.accessDurationMinutes >= 525600
                                ? `${Math.round(selectedCollection.accessDurationMinutes / 525600)} year${Math.round(selectedCollection.accessDurationMinutes / 525600) > 1 ? 's' : ''}`
                                : `${Math.round(selectedCollection.accessDurationMinutes / 259200)} month${Math.round(selectedCollection.accessDurationMinutes / 259200) > 1 ? 's' : ''}`
                              : `${Math.round(selectedCollection.accessDurationMinutes / 129600)} month${Math.round(selectedCollection.accessDurationMinutes / 129600) > 1 ? 's' : ''}`
                            : `${Math.round(selectedCollection.accessDurationMinutes / 43200)} month${Math.round(selectedCollection.accessDurationMinutes / 43200) > 1 ? 's' : ''}`
                          : `${Math.round(selectedCollection.accessDurationMinutes / 1440)} day${Math.round(selectedCollection.accessDurationMinutes / 1440) > 1 ? 's' : ''}`
                        : `${Math.round(selectedCollection.accessDurationMinutes / 60)} hour${Math.round(selectedCollection.accessDurationMinutes / 60) > 1 ? 's' : ''}`
                      : `${selectedCollection.accessDurationMinutes} minute${selectedCollection.accessDurationMinutes > 1 ? 's' : ''}`
                    }`
                  )}
                </p>
              )}
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <input
                  type="email"
                  value={albumEmail}
                  onChange={(e) => setAlbumEmail(e.target.value)}
                  onKeyPress={handleCollectionAuthKeyPress}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              
              <div className="relative">
                <input
                  type={showAlbumPassword ? "text" : "password"}
                  value={albumPassword}
                  onChange={(e) => setAlbumPassword(e.target.value)}
                  onKeyPress={handleCollectionAuthKeyPress}
                  placeholder="Enter collection password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-12"
                />
                <button
                  onClick={() => setShowAlbumPassword(!showAlbumPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showAlbumPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              <button
                onClick={handleCollectionAuth}
                disabled={loading || !albumEmail.trim() || !albumPassword.trim()}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <ShieldCheck className="w-5 h-5" />
                {loading ? 'Authenticating...' : 'Access Collection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Sent View */}
      {currentView === 'verification-sent' && (
        <div className="max-w-md mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Check Your Email
            </h2>
            <p className="text-gray-600 mb-4">
              We've sent a verification link to <strong>{verificationEmail}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Click the link in your email to verify your address and access the album.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setCurrentView('album-auth');
                  setVerificationSent(false);
                  setVerificationEmail('');
                }}
                className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition duration-200"
              >
                Back to Login
              </button>
              <button
                onClick={handleVerificationComplete}
                disabled={loading}
                className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? 'Checking...' : 'I\'ve Verified My Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photos View */}
      {currentView === 'photos' && selectedAlbum && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-6 flex justify-between items-center">
            <div>
              {selectedAlbum.videoLink && (
                <button
                  onClick={() => window.open(selectedAlbum.videoLink, '_blank')}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition duration-200 flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Videos
                </button>
              )}
            </div>
            <div>
              {userType === 'admin' && (
                <button
                  onClick={() => setShowThumbnailSelector(true)}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition duration-200 flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  Set Thumbnail
                </button>
              )}
            </div>
          </div>
          {selectedAlbum.photos && selectedAlbum.photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {selectedAlbum.photos.map(photo => (
                <div
                  key={photo.id}
                  onClick={() => openPhoto(photo)}
                  className="aspect-square overflow-hidden rounded-lg cursor-pointer group bg-gray-100"
                >
                  <img
                    src={photo.thumb}
                    alt="Photo"
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm9yPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Check Your Email
              </h2>
              <p className="text-gray-600 mb-4">
                A verification email has been sent to your inbox! Please wait a few minutes for it to arrive.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                When you click on the verification button, it will take you to the log in page of the album. Enter the email address you are verifying and the album password which I (Tariq lol) have provided. Once you have logged in, the site will continue to recognise your credentials and should not ask you to re-verify. Thanks!
              </p>
            </div>
          )}
        </div>
      )}

      {/* Album Photos View (Manage) */}
      {currentView === 'album-photos' && selectedAlbum && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">{selectedAlbum.name}</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Sort by:</span>
                  <select
                    value={albumPhotosSortOrder}
                    onChange={(e) => setAlbumPhotosSortOrder(e.target.value)}
                    className="text-sm border border-gray-300 rounded px-3 py-1 focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="oldest">Oldest First</option>
                    <option value="newest">Newest First</option>
                    <option value="name">Name</option>
                  </select>
                </div>
                <p className="text-sm text-gray-600">
                  {selectedAlbum.photos?.length || 0} photos
                </p>
              </div>
            </div>
          </div>
          
          {selectedAlbum.photos && selectedAlbum.photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {selectedAlbum.photos.map(photo => (
                <div
                  key={photo.id}
                  onClick={() => openPhoto(photo)}
                  className="aspect-square overflow-hidden rounded-lg cursor-pointer group bg-gray-100"
                >
                  <img
                    src={photo.thumb}
                    alt="Photo"
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm9yPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {new Date(photo.created).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No photos in this album</h3>
              <p className="text-gray-600">This album doesn't contain any photos yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-full max-h-full">
            <img
              src={selectedPhoto.url}
              alt="Full size photo"
              className="max-w-full max-h-full object-contain rounded-lg"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjNDQ0Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iI2FhYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==';
              }}
            />
            
            {selectedAlbum && selectedAlbum.photos && selectedAlbum.photos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition duration-200"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
                
                <button
                  onClick={nextPhoto}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition duration-200"
                >
                  <ArrowLeft className="w-6 h-6 transform rotate-180" />
                </button>
              </>
            )}
            
            <button
              onClick={closePhoto}
              className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition duration-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && shareModalAlbum && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Share Album</h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-2">{shareModalAlbum.name}</p>
              <p className="text-sm text-gray-500">{shareModalAlbum.photo_count} photos</p>
            </div>
            
            {!shareUrl ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link Duration
                  </label>
                  <select
                    value={shareDuration}
                    onChange={(e) => setShareDuration(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="180">3 hours</option>
                    <option value="360">6 hours</option>
                    <option value="720">12 hours</option>
                    <option value="1440">24 hours</option>
                    <option value="2880">48 hours</option>
                    <option value="10080">1 week</option>
                    <option value="43200">1 month</option>
                    <option value="129600">3 months</option>
                    <option value="259200">6 months</option>
                    <option value="525600">1 year</option>
                    <option value="-1">Unlimited</option>
                  </select>
                </div>
                
                <button
                  onClick={handleCreateShare}
                  disabled={loading}
                  className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Share2 className="w-5 h-5" />
                  {loading ? 'Creating Link...' : 'Create Share Link'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Share Link
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                    />
                    <button
                      onClick={handleCopyShareUrl}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition duration-200"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(shareUrl, '_blank')}
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition duration-200 flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Link
                  </button>
                  <button
                    onClick={() => {
                      setShowShareModal(false);
                      setShareUrl('');
                    }}
                    className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

            {/* Thumbnail Selector Modal */}
      {showThumbnailSelector && selectedAlbum && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          {console.log('Thumbnail selector opened with album:', selectedAlbum)}
          {console.log('Album photos:', selectedAlbum.photos)}
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Select Album Thumbnail</h2>
              <button
                onClick={() => {
                  setShowThumbnailSelector(false);
                  setSelectedThumbnail(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-2">{selectedAlbum.name}</p>
              <p className="text-sm text-gray-500">Choose a photo to use as the album thumbnail</p>
            </div>
            
            <div className="overflow-y-auto max-h-[60vh] mb-4">
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {selectedAlbum.photos && selectedAlbum.photos.map(photo => (
                  <div
                    key={photo.id}
                    onClick={() => setSelectedThumbnail(photo)}
                    className={`aspect-square overflow-hidden rounded-lg cursor-pointer group relative ${
                      selectedThumbnail?.id === photo.id ? 'ring-4 ring-indigo-500' : 'ring-2 ring-gray-200'
                    }`}
                  >
                    <img
                      src={photo.thumb}
                      alt={photo.filename}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm9yPC90ZXh0Pjwvc3ZnPg==';
                      }}
                    />
                    {selectedThumbnail?.id === photo.id && (
                      <div className="absolute top-2 right-2 bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                      <p className="text-white text-xs truncate">{photo.filename}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (selectedThumbnail) {
                    console.log('Selected thumbnail:', selectedThumbnail);
                    // Use thumb if url is not available
                    const coverPhotoUrl = selectedThumbnail.url || selectedThumbnail.thumb;
                    console.log('Using cover photo URL:', coverPhotoUrl);
                    updateAlbumThumbnail(selectedAlbum.id, coverPhotoUrl);
                  }
                }}
                disabled={!selectedThumbnail || loading}
                className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Check className="w-5 h-5" />
                {loading ? 'Updating...' : 'Set as Thumbnail'}
              </button>
              <button
                onClick={() => {
                  setShowThumbnailSelector(false);
                  setSelectedThumbnail(null);
                }}
                className="flex-1 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Manager Modal */}
      {showUserManager && userManagerAlbum && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Manage Verified Users</h2>
              <button
                onClick={() => {
                  setShowUserManager(false);
                  setUserManagerAlbum(null);
                  setVerifiedUsers([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-2">{userManagerAlbum.name}</p>
              <p className="text-sm text-gray-500">
                Remove users to revoke their access. They will need to verify their email again to access this album.
              </p>
            </div>
            
            <div className="overflow-y-auto max-h-[50vh] mb-4">
              {verifiedUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No verified users for this album</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {verifiedUsers.map((user, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{user.email}</p>
                        <p className="text-sm text-gray-500">
                          Verified: {new Date(user.created_date).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          if (confirm(`Are you sure you want to remove ${user.email} from this album? They will need to verify their email again to access it.`)) {
                            removeVerifiedUser(userManagerAlbum.id, user.email);
                          }
                        }}
                        className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200 transition duration-200 flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowUserManager(false);
                  setUserManagerAlbum(null);
                  setVerifiedUsers([]);
                }}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared Link Manager Modal */}
      {showSharedLinkManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Manage Shared Links</h2>
              <button
                onClick={() => {
                  setShowSharedLinkManager(false);
                  setSharedLinks([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                View and manage all shared links. Delete links to revoke access immediately.
              </p>
            </div>
            
            <div className="overflow-y-auto max-h-[60vh] mb-4">
              {sharedLinks.length === 0 ? (
                <div className="text-center py-8">
                  <Share2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No shared links found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sharedLinks.map((link, index) => {
                    const isExpired = new Date(link.expires_at) < new Date();
                    const accessDurationMinutes = Math.round(link.access_duration / (60 * 1000));
                    const durationText = link.access_duration === -1 ? 'Unlimited' : 
                      accessDurationMinutes >= 60 
                        ? accessDurationMinutes >= 1440 
                          ? accessDurationMinutes >= 43200
                            ? accessDurationMinutes >= 129600
                              ? accessDurationMinutes >= 259200
                                ? accessDurationMinutes >= 525600
                                  ? `${Math.round(accessDurationMinutes / 525600)}y`
                                  : `${Math.round(accessDurationMinutes / 259200)}mo`
                                : `${Math.round(accessDurationMinutes / 129600)}mo`
                              : `${Math.round(accessDurationMinutes / 43200)}mo`
                            : `${Math.round(accessDurationMinutes / 1440)}d`
                          : `${Math.round(accessDurationMinutes / 60)}h`
                        : `${accessDurationMinutes}m`;
                    
                    return (
                      <div key={index} className={`p-4 rounded-lg border ${isExpired ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-900">{link.album_name}</h3>
                              {isExpired && (
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                                  Expired
                                </span>
                              )}
                              {link.access_count > 0 && (
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                  {link.access_count} access{link.access_count > 1 ? 'es' : ''}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Token:</span> {link.token}
                              </div>
                              <div>
                                <span className="font-medium">Duration:</span> {durationText}
                              </div>
                              <div>
                                <span className="font-medium">Expires:</span> {new Date(link.expires_at).toLocaleString()}
                              </div>
                            </div>
                            {link.last_accessed && (
                              <div className="text-sm text-gray-500 mt-1">
                                Last accessed: {new Date(link.last_accessed).toLocaleString()}
                              </div>
                            )}
                            {link.email && (
                              <div className="text-sm text-gray-500 mt-1">
                                Email: {link.email}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => {
                                const shareUrl = `https://tfgallery.ggjk89v8x5.synology.me/shared/${link.token}`;
                                copyToClipboard(shareUrl);
                              }}
                              className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm hover:bg-blue-200 transition duration-200 flex items-center gap-1"
                              title="Copy link to clipboard"
                            >
                              <Copy className="w-4 h-4" />
                              Copy
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(`Are you sure you want to delete this shared link for "${link.album_name}"? This will immediately revoke access.`)) {
                                  deleteSharedLink(link.token);
                                }
                              }}
                              className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200 transition duration-200 flex items-center gap-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowSharedLinkManager(false);
                  setSharedLinks([]);
                }}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Manager Modal */}
      {showPasswordManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Album Passwords</h2>
              <button
                onClick={() => {
                  setShowPasswordManager(false);
                  setAlbumPasswords([]);
                  setPasswordVisibility({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                View and manage passwords for all protected albums. Click the eye icon to reveal passwords, or copy them to clipboard.
              </p>
            </div>
            
            <div className="overflow-y-auto max-h-[60vh] mb-4">
              {albumPasswords.length === 0 ? (
                <div className="text-center py-8">
                  <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No protected albums found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {albumPasswords.map((album, index) => (
                    <div key={index} className="p-4 rounded-lg border bg-white border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{album.name}</h3>
                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                              Protected
                            </span>
                          </div>
                          <div className="space-y-2 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Password:</span>
                              <div className="flex items-center gap-2 bg-gray-100 rounded px-2 py-1">
                                <span className="font-mono text-sm">
                                  {passwordVisibility[album.id] ? album.password : '••••••••'}
                                </span>
                                <button
                                  onClick={() => togglePasswordVisibility(album.id)}
                                  className="text-gray-500 hover:text-gray-700 transition-colors"
                                  title={passwordVisibility[album.id] ? 'Hide password' : 'Show password'}
                                >
                                  {passwordVisibility[album.id] ? (
                                    <EyeOff className="w-4 h-4" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => {
                                    copyToClipboard(album.password);
                                  }}
                                  className="text-blue-600 hover:text-blue-700 transition-colors"
                                  title="Copy password to clipboard"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <div>
                                <span className="font-medium">Photos:</span> {album.photo_count || 0}
                              </div>
                              <div>
                                <span className="font-medium">Access Duration:</span> {
                                  album.access_duration ? 
                                    album.access_duration >= 86400000 ? 
                                      `${Math.round(album.access_duration / 86400000)} days` :
                                      album.access_duration >= 3600000 ?
                                        `${Math.round(album.access_duration / 3600000)} hours` :
                                        `${Math.round(album.access_duration / 60000)} minutes`
                                    : 'Default'
                                }
                              </div>
                            </div>
                          </div>
                          {album.created_date && (
                            <div className="text-sm text-gray-500 mt-1">
                              Created: {new Date(album.created_date).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowPasswordManager(false);
                  setAlbumPasswords([]);
                  setPasswordVisibility({});
                }}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection Password Manager Modal */}
      {showCollectionPasswordManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Collection Passwords</h2>
              <button
                onClick={() => {
                  setShowCollectionPasswordManager(false);
                  setCollectionPasswords([]);
                  setCollectionPasswordVisibility({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                View and manage passwords for all protected collections. Click the eye icon to reveal passwords, or copy them to clipboard.
              </p>
            </div>
            
            <div className="overflow-y-auto max-h-[60vh] mb-4">
              {collectionPasswords.length === 0 ? (
                <div className="text-center py-8">
                  <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No protected collections found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {collectionPasswords.map((collection, index) => (
                    <div key={index} className="p-4 rounded-lg border bg-white border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{collection.name}</h3>
                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                              Protected
                            </span>
                          </div>
                          <div className="space-y-2 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Password:</span>
                              <div className="flex items-center gap-2 bg-gray-100 rounded px-2 py-1">
                                <span className="font-mono text-sm">
                                  {collectionPasswordVisibility[collection.id] ? collection.password : '••••••••'}
                                </span>
                                <button
                                  onClick={() => toggleCollectionPasswordVisibility(collection.id)}
                                  className="text-gray-500 hover:text-gray-700 transition-colors"
                                  title={collectionPasswordVisibility[collection.id] ? 'Hide password' : 'Show password'}
                                >
                                  {collectionPasswordVisibility[collection.id] ? (
                                    <EyeOff className="w-4 h-4" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => {
                                    copyToClipboard(collection.password);
                                  }}
                                  className="text-blue-600 hover:text-blue-700 transition-colors"
                                  title="Copy password to clipboard"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <div>
                                <span className="font-medium">Albums:</span> {collection.album_count || 0}
                              </div>
                              <div>
                                <span className="font-medium">Access Duration:</span> {
                                  collection.access_duration ? 
                                    collection.access_duration >= 86400000 ? 
                                      `${Math.round(collection.access_duration / 86400000)} days` :
                                      collection.access_duration >= 3600000 ?
                                        `${Math.round(collection.access_duration / 3600000)} hours` :
                                        `${Math.round(collection.access_duration / 60000)} minutes`
                                    : 'Default'
                                }
                              </div>
                              <div>
                                <span className="font-medium">Description:</span> {collection.description || 'No description'}
                              </div>
                            </div>
                          </div>
                          {collection.created_date && (
                            <div className="text-sm text-gray-500 mt-1">
                              Created: {new Date(collection.created_date).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowCollectionPasswordManager(false);
                  setCollectionPasswords([]);
                  setCollectionPasswordVisibility({});
                }}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection Shared Links Modal */}
      {showCollectionSharedLinksModal && selectedCollectionForManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Manage Shared Links</h2>
              <button
                onClick={() => {
                  setShowCollectionSharedLinksModal(false);
                  setSelectedCollectionForManagement(null);
                  setCollectionSharedLinks([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                View and manage all shared links. Delete links to revoke access immediately.
              </p>
            </div>
            
            <div className="overflow-y-auto max-h-[60vh] mb-4">
              {collectionSharedLinks.length === 0 ? (
                <div className="text-center py-8">
                  <Share2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No shared links found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {collectionSharedLinks.map((link, index) => {
                    const isExpired = new Date(link.expires_at) < new Date();
                    const accessDurationMinutes = Math.round(link.access_duration / (60 * 1000));
                    const durationText = link.access_duration === -1 ? 'Unlimited' : 
                      accessDurationMinutes >= 60 
                        ? accessDurationMinutes >= 1440 
                          ? accessDurationMinutes >= 43200
                            ? accessDurationMinutes >= 129600
                              ? accessDurationMinutes >= 259200
                                ? accessDurationMinutes >= 525600
                                  ? `${Math.round(accessDurationMinutes / 525600)}y`
                                  : `${Math.round(accessDurationMinutes / 259200)}mo`
                                : `${Math.round(accessDurationMinutes / 129600)}mo`
                              : `${Math.round(accessDurationMinutes / 43200)}mo`
                            : `${Math.round(accessDurationMinutes / 1440)}d`
                          : `${Math.round(accessDurationMinutes / 60)}h`
                        : `${accessDurationMinutes}m`;
                    
                    return (
                      <div key={index} className={`p-4 rounded-lg border ${isExpired ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-900">{link.collection_name}</h3>
                              {isExpired && (
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                                  Expired
                                </span>
                              )}
                              {link.access_count > 0 && (
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                  {link.access_count} access{link.access_count > 1 ? 'es' : ''}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Token:</span> {link.token}
                              </div>
                              <div>
                                <span className="font-medium">Duration:</span> {durationText}
                              </div>
                              <div>
                                <span className="font-medium">Expires:</span> {new Date(link.expires_at).toLocaleString()}
                              </div>
                            </div>
                            {link.last_accessed && (
                              <div className="text-sm text-gray-500 mt-1">
                                Last accessed: {new Date(link.last_accessed).toLocaleString()}
                              </div>
                            )}
                            {link.email && (
                              <div className="text-sm text-gray-500 mt-1">
                                Email: {link.email}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => {
                                const shareUrl = `https://tfgallery.ggjk89v8x5.synology.me/shared/collection/${link.token}`;
                                copyToClipboard(shareUrl);
                              }}
                              className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm hover:bg-blue-200 transition duration-200 flex items-center gap-1"
                              title="Copy link to clipboard"
                            >
                              <Copy className="w-4 h-4" />
                              Copy
                            </button>
                            <button
                              onClick={() => handleDeleteCollectionSharedLink(link.token)}
                              className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200 transition duration-200 flex items-center gap-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowCollectionSharedLinksModal(false);
                  setSelectedCollectionForManagement(null);
                  setCollectionSharedLinks([]);
                }}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection Verified Users Modal */}
      {showCollectionVerifiedUsersModal && selectedCollectionForManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Manage Verified Users</h2>
              <button
                onClick={() => {
                  setShowCollectionVerifiedUsersModal(false);
                  setSelectedCollectionForManagement(null);
                  setCollectionVerifiedUsers([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-2">{selectedCollectionForManagement.name}</p>
              <p className="text-sm text-gray-500">
                Remove users to revoke their access. They will need to verify their email again to access this collection.
              </p>
            </div>
            
            <div className="overflow-y-auto max-h-[50vh] mb-4">
              {collectionVerifiedUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No verified users for this collection</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {collectionVerifiedUsers.map((user, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{user.email}</p>
                        <p className="text-sm text-gray-500">
                          Verified: {new Date(user.created_date).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveCollectionVerifiedUser(user.email)}
                        className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200 transition duration-200 flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowCollectionVerifiedUsersModal(false);
                  setSelectedCollectionForManagement(null);
                  setCollectionVerifiedUsers([]);
                }}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Link Management Modal */}
      {showVideoLinkModal && videoLinkAlbum && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Manage Video Links</h2>
              <button
                onClick={closeVideoLinkModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-2">{videoLinkAlbum.name}</p>
              <p className="text-sm text-gray-500">
                Enter a URL that will take users to a page containing videos for this album. When set, a "Videos" button will appear in the album view.
              </p>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video URL
              </label>
              <input
                type="url"
                value={videoLinkUrl}
                onChange={(e) => setVideoLinkUrl(e.target.value)}
                placeholder="https://example.com/videos"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to remove video link</p>
            </div>
            
            <div className="flex gap-2">
              {videoLinkAlbum.videoLink && (
                <button
                  onClick={deleteVideoLink}
                  disabled={loading}
                  className="flex-1 bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition duration-200 disabled:opacity-50"
                >
                  {loading ? 'Removing...' : 'Remove Link'}
                </button>
              )}
              <button
                onClick={closeVideoLinkModal}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={saveVideoLink}
                disabled={loading}
                className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition duration-200 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Access Logs Modal */}
      {showLogsModal && logsAlbum && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Access Logs</h2>
              <button
                onClick={closeLogsModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-2">{logsAlbum.name}</p>
              <p className="text-sm text-gray-500">
                View access logs for this protected album including email verifications and shared link access.
              </p>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">Loading access logs...</div>
              </div>
            ) : (
              <div className="overflow-y-auto max-h-96">
                {accessLogs.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No access logs found for this album.</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Date</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Access Type</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">IP Address</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {accessLogs.map((log, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            {new Date(log.access_date).toLocaleString()}
                          </td>
                          <td className="px-4 py-2">{log.email || 'N/A'}</td>
                          <td className="px-4 py-2">
                            {log.shared_token ? 'Shared Link' : 'Email Verification'}
                          </td>
                          <td className="px-4 py-2">{log.ip_address || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            
            <div className="flex justify-end mt-4">
              <button
                onClick={closeLogsModal}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
